import { db } from '../config/firebase';
import { doc, collection, updateDoc, arrayUnion, getDoc, setDoc, getDocs } from 'firebase/firestore';

export interface DeviceInfo {
  deviceId: string;
  deviceName: string;
  platform: {
    userAgent: string;
    platform: string;
    type: string;
    brand: string;
    model: string;
    version: string;
  };
  capabilities: {
    webauthn: boolean;
    biometric: boolean;
    platform: boolean;
    secureContext: boolean;
    crossOriginIsolated: boolean;
  };
  firstSeen: string;
  lastSeen: string;
  authMethods: string[];
}

export interface UserDevices {
  devices: DeviceInfo[];
}

class DeviceInfoService {
  private async generateDeviceId(): Promise<string> {
    const fingerprint = await this.getDeviceFingerprint();
    const buffer = await crypto.subtle.digest('SHA-256', 
      new TextEncoder().encode(fingerprint));
    return Array.from(new Uint8Array(buffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  private async getDeviceFingerprint(): Promise<string> {
    const components = [
      navigator.userAgent,
      navigator.language,
      new Date().getTimezoneOffset(),
      window.screen.colorDepth,
      navigator.hardwareConcurrency,
      `${window.screen.width}x${window.screen.height}`
    ];

    if ('userAgentData' in navigator) {
      const uaData = await (navigator as any).userAgentData.getHighEntropyValues([
        'platform',
        'platformVersion',
        'architecture',
        'model',
        'uaFullVersion'
      ]);
      components.push(JSON.stringify(uaData));
    }

    return components.join('|');
  }

  async getDeviceInfo(): Promise<DeviceInfo> {
    try {
      const platform = await this.getPlatformInfo();
      const capabilities = await this.getCapabilities();
      const deviceId = await this.generateDeviceId();
      const deviceName = this.generateDeviceName(platform);
      
      const deviceInfo: DeviceInfo = {
        deviceId,
        deviceName,
        platform,
        capabilities,
        firstSeen: new Date().toISOString(),
        lastSeen: new Date().toISOString(),
        authMethods: []
      };

      return deviceInfo;
    } catch (error) {
      console.error('Error getting device info:', error);
      throw new Error('Failed to get device information');
    }
  }

  private generateDeviceName(platform: DeviceInfo['platform']): string {
    const parts = [];
    
    // Add brand if available and not generic
    if (platform.brand && platform.brand !== 'unknown') {
      parts.push(platform.brand);
    }
    
    // Add model if available and not generic
    if (platform.model && platform.model !== 'unknown') {
      parts.push(platform.model);
    }
    
    // Add platform type if available and not generic
    if (platform.type && platform.type !== 'unknown') {
      parts.push(platform.type);
    }
    
    // If we have no specific info, use the platform string
    if (parts.length === 0) {
      parts.push(platform.platform);
    }
    
    return parts.join(' ');
  }

  private async getPlatformInfo(): Promise<DeviceInfo['platform']> {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    
    // Get basic platform info that's always available
    const info: DeviceInfo['platform'] = {
      userAgent,
      platform,
      type: 'unknown',
      brand: 'unknown',
      model: 'unknown',
      version: 'unknown'
    };

    // Try to get more detailed info if available
    try {
      if ('userAgentData' in navigator) {
        const uaData = (navigator as any).userAgentData;
        if (uaData) {
          const platformInfo = await uaData.getHighEntropyValues(['platform', 'platformVersion', 'model', 'brand']);
          
          if (platformInfo) {
            info.type = platformInfo.platform || info.type;
            info.brand = platformInfo.brand || info.brand;
            info.model = platformInfo.model || info.model;
            info.version = platformInfo.platformVersion || info.version;
          }
        }
      }
    } catch (error) {
      console.warn('Could not get detailed platform info:', error);
      // Continue with basic info
    }

    return info;
  }

  private async getCapabilities(): Promise<DeviceInfo['capabilities']> {
    return {
      webauthn: 'credentials' in navigator && 'PublicKeyCredential' in window,
      biometric: await this.hasBiometricCapability(),
      platform: 'platform' in navigator,
      secureContext: window.isSecureContext,
      crossOriginIsolated: window.crossOriginIsolated
    };
  }

  private async hasBiometricCapability(): Promise<boolean> {
    if (!window.PublicKeyCredential) {
      return false;
    }

    try {
      return await (PublicKeyCredential as any)
        .isUserVerifyingPlatformAuthenticatorAvailable();
    } catch {
      return false;
    }
  }

  async registerDeviceForUser(userId: string, authMethod: string): Promise<DeviceInfo> {
    const deviceInfo = await this.getDeviceInfo();
    console.log('Registering device for user:', userId);
    console.log('Device info:', deviceInfo);

    try {
      // Store in user's devices subcollection
      const userDevicesRef = collection(db, 'users', userId, 'devices');
      const deviceDoc = doc(userDevicesRef, deviceInfo.deviceId);
      
      const docSnap = await getDoc(deviceDoc);
      
      if (!docSnap.exists()) {
        // Create new device document
        await setDoc(deviceDoc, {
          ...deviceInfo,
          authMethods: [authMethod]
        });
        console.log('Created new device document');
      } else {
        // Update existing device
        const existingDevice = docSnap.data() as DeviceInfo;
        const uniqueMethods = new Set([...existingDevice.authMethods, authMethod]);
        
        await updateDoc(deviceDoc, {
          lastSeen: new Date().toISOString(),
          authMethods: Array.from(uniqueMethods)
        });
        console.log('Updated existing device document');
      }

      return deviceInfo;
    } catch (error) {
      console.error('Error registering device:', error);
      throw new Error('Failed to register device');
    }
  }

  async getUserDevices(userId: string): Promise<DeviceInfo[]> {
    console.log('Getting devices for user:', userId);
    
    try {
      const userDevicesRef = collection(db, 'users', userId, 'devices');
      const snapshot = await getDocs(userDevicesRef);
      
      const devices = snapshot.docs.map(doc => doc.data() as DeviceInfo);
      console.log('Found devices:', devices.length);
      
      return devices;
    } catch (error) {
      console.error('Error getting user devices:', error);
      throw new Error('Failed to get user devices');
    }
  }
}

export const deviceInfoService = new DeviceInfoService();
export default deviceInfoService;
